import type { CaptureResult } from "posthog-js";

/**
 * URL params that are credentials. Ankur (#bugs 09-23): Supabase access and
 * refresh tokens sat in `$current_url` on ~578 staging/localhost events (a
 * magic link's `#access_token=…&refresh_token=…` landing), and OAuth `?code=`
 * values on ~169 production events.
 *
 * The static landing pages (public/landing-d6/*.html) start PostHog in their
 * own inline script and carry a copy of this list; a test holds them equal.
 */
export const SENSITIVE_URL_PARAMS = [
  "code",
  "state",
  "access_token",
  "refresh_token",
  "provider_token",
  "provider_refresh_token",
  "id_token",
  "token",
  "token_hash",
] as const;

/**
 * The event properties that carry a URL, on the event and on the person. The
 * `$session_entry_*` pair is posthog-js's copy of the session's first URL,
 * merged into EVERY event of the session: a magic-link landing would otherwise
 * ride all of them.
 */
const URL_PROPERTIES = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_referrer",
] as const;

const SENSITIVE = new Set<string>(SENSITIVE_URL_PARAMS);

/** A malformed escape must not throw inside `before_send`: that drops the event. */
function decodeKey(key: string): string {
  try {
    return decodeURIComponent(key);
  } catch {
    return key;
  }
}

/** One `a=1&b=2` run with every sensitive value masked, or null if none was. */
function maskParams(run: string): string | null {
  let hit = false;
  const masked = run
    .split("&")
    .map((pair) => {
      const eq = pair.indexOf("=");
      const key = eq === -1 ? pair : pair.slice(0, eq);
      if (eq === -1 || !SENSITIVE.has(decodeKey(key))) return pair;
      hit = true;
      return `${key}=redacted`;
    })
    .join("&");
  return hit ? masked : null;
}

/**
 * The URL with every credential's value masked, in the query and in the hash.
 * A URL with none comes back byte-identical (no re-encoding), so every other
 * breakdown reads exactly as before.
 */
export function redactUrl(url: string): string {
  const hashAt = url.indexOf("#");
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? null : url.slice(hashAt + 1);
  const queryAt = beforeHash.indexOf("?");

  let out = beforeHash;
  if (queryAt !== -1) {
    const masked = maskParams(beforeHash.slice(queryAt + 1));
    if (masked !== null) out = `${beforeHash.slice(0, queryAt + 1)}${masked}`;
  }
  if (hash !== null) {
    out += `#${maskParams(hash) ?? hash}`;
  }
  return out;
}

function redactProperties(props: Record<string, unknown> | undefined): void {
  if (!props) return;
  for (const key of URL_PROPERTIES) {
    const value = props[key];
    if (typeof value === "string") props[key] = redactUrl(value);
  }
}

/** posthog-js `before_send`: every event, and the person properties it sets. */
export function redactCapture(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event) return event;
  redactProperties(event.properties);
  redactProperties(event.properties?.$set as Record<string, unknown>);
  redactProperties(event.properties?.$set_once as Record<string, unknown>);
  redactProperties(event.$set);
  redactProperties(event.$set_once);
  return event;
}
